import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/context/CartContext";
import { money } from "@/config";

export function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, count } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex w-full flex-col bg-cream sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-cocoa">
            Tu carrito {count > 0 && <span className="text-habanero">({count})</span>}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
            <p className="font-script font-semibold text-3xl text-clay">Tu carrito está vacío...</p>
            <p className="text-sm text-clay">¡Pero tu mesa no tiene por qué estarlo!</p>
            <Button
              data-testid="cart-go-shop-button"
              className="rounded-full bg-habanero text-white hover:bg-habanero-dark"
              onClick={() => {
                setOpen(false);
                navigate("/tienda");
              }}
            >
              Ver la tienda
            </Button>
          </div>
        ) : (
          <>
            <div className="mt-4 flex-1 space-y-4 overflow-y-auto pr-1">
              {items.map((i) => (
                <div key={`${i.slug}-${i.variant_id}`} className="flex gap-3" data-testid={`cart-item-${i.slug}-${i.variant_id}`}>
                  <img src={i.image} alt={i.name} className="h-16 w-16 rounded-lg border border-sand object-cover" />
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-bold text-cocoa">{i.name}</p>
                        <p className="text-xs text-clay">{i.variant_label}</p>
                      </div>
                      <button
                        data-testid={`cart-remove-${i.slug}-${i.variant_id}`}
                        aria-label={`Quitar ${i.name}`}
                        className="text-clay transition-colors hover:text-destructive"
                        onClick={() => removeItem(i.slug, i.variant_id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center gap-2 rounded-full border border-sand px-1 py-0.5">
                        <button
                          data-testid={`cart-minus-${i.slug}-${i.variant_id}`}
                          aria-label="Disminuir cantidad"
                          className="p-1 text-clay hover:text-habanero"
                          onClick={() => updateQty(i.slug, i.variant_id, i.qty - 1)}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="min-w-5 text-center text-sm font-bold">{i.qty}</span>
                        <button
                          data-testid={`cart-plus-${i.slug}-${i.variant_id}`}
                          aria-label="Aumentar cantidad"
                          className="p-1 text-clay hover:text-habanero"
                          onClick={() => updateQty(i.slug, i.variant_id, i.qty + 1)}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <span className="text-sm font-bold text-cocoa">{money(i.price * i.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-4">
              <Separator className="bg-sand" />
              <div className="flex items-center justify-between py-4">
                <span className="text-sm font-semibold text-clay">Subtotal</span>
                <span className="font-display text-xl font-bold text-cocoa" data-testid="cart-subtotal">
                  {money(subtotal)}
                </span>
              </div>
              <Button
                data-testid="cart-checkout-button"
                className="w-full rounded-full bg-habanero py-6 text-base font-bold text-white transition-colors hover:bg-habanero-dark"
                onClick={() => {
                  setOpen(false);
                  navigate("/checkout");
                }}
              >
                Ir a pagar
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
